import React, { useEffect, useMemo, useState } from "react";

import { Spin } from "antd";
import moment from "moment";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import LocationToolbar from "../components/multi-location/LocationToolbar";
import TestSpendPeriodControl from "../components/TestSpendPeriodControl";
import TitledPanel from "../components/TitledPanel";
// import UsageMetricsOffersPanel from "../components/UsageMetricsOffersPanel";
import {
  MULTI_LOCATION_OPTIONS,
  USAGE_CREDITS_CATEGORY_MAP,
  USAGE_CREDITS_CATEGORY_META,
} from "../constants";
import PlanTestUsage from "./components/PlanTestUsage";
import StartTopupPoolAlertModal from "./components/StartTopupPoolAlertModal";
import SubscriptionCategoryUsage from "./components/SubscriptionCategoryUsage";
import SubscriptionSummaryCards from "./components/SubscriptionSummaryCards";
import TermSummary from "./components/TermSummary";
import TestSpendByMonth from "./components/TestSpendByMonth";

import { getUserCredentials } from "../../../../utils/common";
import {
  areSubscriptionCreditsExhausted,
  getStartTopupPoolAlertPending,
  getTotalTopUpAvailableCredits,
  hasStartTopupPoolAlertBeenShownThisMonth,
  mapCategoryCreditsToPlanUsage,
  mapUsageCreditsToCategoryUsage,
  mapUsageOutstandingToSummaryCards,
  markStartTopupPoolAlertShownThisMonth,
} from "../utils";

import {
  fetchUsageCredits,
  fetchUsageOutstanding,
  getDefaultCreditsDateRange,
} from "../../../../actions/usage";

import { designTokens } from "../../../../styles/tokens";

import "./SubscriptionDashboard.css";

const PLAN_USAGE_CATEGORY_COLORS = {
  microbiology: designTokens.colorUsageCategoryMicrobiology,
  chemistry: designTokens.colorUsageCategoryChemistry,
  environment: designTokens.colorUsageCategoryEnvironment,
};

const ALL_LOCATIONS_KEY = "all";
const START_TOPUP_POOL_ALERT_DURATION_MS = 3000;

const formatCreditsPeriodLabel = (dateRange) => {
  if (!dateRange || !dateRange.includes(",")) return "";
  const [start, end] = dateRange.split(",");
  if (!start || !end) return "";
  return `${moment(start).format("MMM D, YYYY")} - ${moment(end).format("MMM D, YYYY")}`;
};

const buildPeriodDateRange = (start, end) => {
  const startMoment = moment(start);
  const endMoment = moment(end);
  if (!startMoment.isValid() || !endMoment.isValid()) return null;
  return `${startMoment.format("YYYY-MM-DD")},${endMoment.format("YYYY-MM-DD")}`;
};

const parseCreditsDateRange = (dateRange) => {
  if (!dateRange || !dateRange.includes(",")) return null;
  const [startDate, endDate] = dateRange.split(",");
  if (!startDate || !endDate) return null;
  return { startDate, endDate };
};

export default function SubscriptionDashboard({
  // onViewAllOffers,
  onTopupTests,
  locations = MULTI_LOCATION_OPTIONS,
  selectedLocationKey = ALL_LOCATIONS_KEY,
  onSelectLocation,
  onLocationsLoaded,
  onCategoryCreditsLoaded,
  suppressStartTopupPoolAlert = false,
}) {
  const history = useHistory();
  const showTopUp = true;
  const [categoryCredits, setCategoryCredits] = useState([]);
  const [outstandingSummary, setOutstandingSummary] = useState({});
  const [creditsDateRange, setCreditsDateRange] = useState(null);
  const [outstandingLoading, setOutstandingLoading] = useState(true);
  const [creditsLoading, setCreditsLoading] = useState(true);
  const [spendDateRange, setSpendDateRange] = useState(null);
  const [topupPoolAlertOpen, setTopupPoolAlertOpen] = useState(false);
  const [topupPoolAlertTests, setTopupPoolAlertTests] = useState(0);

  const periodLabel = useMemo(
    () => formatCreditsPeriodLabel(creditsDateRange),
    [creditsDateRange],
  );
  const outstandingCompanyName = selectedLocationKey === ALL_LOCATIONS_KEY
    ? undefined
    : (locations.find((loc) => loc.key === selectedLocationKey)?.label || selectedLocationKey);
  const billingSpendRange = useMemo(
    () => parseCreditsDateRange(creditsDateRange),
    [creditsDateRange],
  );
  const effectiveSpendRange = spendDateRange || billingSpendRange;
  const dashboardLoading = outstandingLoading || creditsLoading || !creditsDateRange;

  useEffect(() => {
    const controller = new AbortController();
    let isMounted = true;

    const loadOutstanding = async () => {
      setOutstandingLoading(true);
      const response = await fetchUsageOutstanding({
        signal: controller.signal,
        companyName: outstandingCompanyName,
      });

      if (response.canceled || !isMounted) return;

      if (!response.success) {
        toast.error(response.message || "Unable to load outstanding details.");
        setOutstandingSummary({});
        setCreditsDateRange((current) => current || getDefaultCreditsDateRange());
        setOutstandingLoading(false);
        return;
      }

      const result = response.result || {};
      setOutstandingSummary(mapUsageOutstandingToSummaryCards({
        ...result,
        subscription_mode: result.subscription_mode ?? response.subscription_mode,
      }));

      const billingPeriodRange = buildPeriodDateRange(
        result.current_period_start,
        result.current_period_end,
      );
      setCreditsDateRange((current) => current || billingPeriodRange || getDefaultCreditsDateRange());
      setOutstandingLoading(false);
    };

    loadOutstanding();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [outstandingCompanyName]);

  useEffect(() => {
    if (!creditsDateRange) return undefined;

    const controller = new AbortController();
    let isMounted = true;

    const loadCredits = async () => {
      setCreditsLoading(true);
      const user = getUserCredentials() || {};
      const isAllLocations = selectedLocationKey === ALL_LOCATIONS_KEY;
      const response = await fetchUsageCredits({
        locationName: isAllLocations ? user.company_name : selectedLocationKey,
        dateRange: creditsDateRange,
        allLocation: isAllLocations,
        signal: controller.signal,
      });

      if (response.canceled || !isMounted) return;

      if (response.success) {
        const mappedCredits = mapUsageCreditsToCategoryUsage({
          result: { credits: response.credits || [] },
        }, {
          categoryMap: USAGE_CREDITS_CATEGORY_MAP,
          categoryMeta: USAGE_CREDITS_CATEGORY_META,
        });
        setCategoryCredits(mappedCredits);
        if (typeof onCategoryCreditsLoaded === "function") {
          onCategoryCreditsLoaded(mappedCredits);
        }
        if (typeof onLocationsLoaded === "function" && response.locations_list?.length) {
          onLocationsLoaded(response.locations_list);
        }
      } else {
        toast.error(response.message || "Unable to load credits.");
        setCategoryCredits([]);
        if (typeof onCategoryCreditsLoaded === "function") {
          onCategoryCreditsLoaded([]);
        }
      }
      setCreditsLoading(false);
    };

    loadCredits();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [selectedLocationKey, creditsDateRange, onCategoryCreditsLoaded, onLocationsLoaded]);

  useEffect(() => {
    if (!billingSpendRange) return;
    setSpendDateRange(billingSpendRange);
  }, [billingSpendRange]);

  const handlePeriodChange = (startDate, endDate) => {
    if (!startDate && !endDate) {
      const billingPeriodRange = buildPeriodDateRange(
        outstandingSummary.spendPeriodStart,
        outstandingSummary.spendPeriodEnd,
      );
      setCreditsDateRange(billingPeriodRange || getDefaultCreditsDateRange());
      return;
    }
    setCreditsDateRange(`${startDate},${endDate || startDate}`);
  };

  const subscriptionPlanUsage = useMemo(
    () => mapCategoryCreditsToPlanUsage(
      categoryCredits,
      PLAN_USAGE_CATEGORY_COLORS,
      { poolType: "subscription" },
    ),
    [categoryCredits],
  );

  const topupPlanUsage = useMemo(
    () => mapCategoryCreditsToPlanUsage(
      categoryCredits,
      PLAN_USAGE_CATEGORY_COLORS,
      { poolType: "topup" },
    ),
    [categoryCredits],
  );

  const isTopupPlan = outstandingSummary.planType === "topup";
  const topUpAvailable = useMemo(
    () => getTotalTopUpAvailableCredits(categoryCredits),
    [categoryCredits],
  );
  const subscriptionCreditsExhausted = useMemo(
    () => areSubscriptionCreditsExhausted(categoryCredits),
    [categoryCredits],
  );

  useEffect(() => {
    if (dashboardLoading || suppressStartTopupPoolAlert) {
      return;
    }

    const pending = getStartTopupPoolAlertPending();
    if (!pending) {
      return;
    }

    if (hasStartTopupPoolAlertBeenShownThisMonth()) {
      markStartTopupPoolAlertShownThisMonth();
      return;
    }

    const loadedPoolTests = topUpAvailable || topupPlanUsage.total;
    const poolTests = pending.poolTests || loadedPoolTests;
    if (poolTests <= 0) {
      return;
    }

    markStartTopupPoolAlertShownThisMonth();
    setTopupPoolAlertTests(poolTests);
    setTopupPoolAlertOpen(true);
  }, [
    dashboardLoading,
    suppressStartTopupPoolAlert,
    topUpAvailable,
    topupPlanUsage.total,
  ]);

  useEffect(() => {
    if (!topupPoolAlertOpen) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setTopupPoolAlertOpen(false);
    }, START_TOPUP_POOL_ALERT_DURATION_MS);

    return () => window.clearTimeout(timeoutId);
  }, [topupPoolAlertOpen]);

  const summaryCards = useMemo(() => ({
    ...outstandingSummary,
    topUpMode: isTopupPlan,
    testsUsed: subscriptionPlanUsage.used,
    testsTotal: subscriptionPlanUsage.total,
    totalCredits: subscriptionPlanUsage.total,
    monthlyTestCredits: subscriptionPlanUsage.total,
    topUpAvailable,
    subscriptionCreditsExhausted,
  }), [
    outstandingSummary,
    isTopupPlan,
    subscriptionPlanUsage.used,
    subscriptionPlanUsage.total,
    topUpAvailable,
    subscriptionCreditsExhausted,
  ]);

  return (
    <div className="UsageMetricsSubscriptionDashboard">
      <LocationToolbar
        locations={locations}
        selectedLocationKey={selectedLocationKey}
        onSelectLocation={onSelectLocation}
        periodLabel={periodLabel}
        onPeriodChange={handlePeriodChange}
      />
      {dashboardLoading && (
        <div className="UsageMetricsSubscriptionDashboard__Loading">
          <Spin size="large" />
        </div>
      )}
      {!dashboardLoading && (
        <>
          <SubscriptionSummaryCards
            showTopUp={showTopUp}
            onTopUp={onTopupTests}
            onViewBillingDetails={() => history.push("/myaccount/invoice")}
            summaryCards={summaryCards}
          />
          <PlanTestUsage
            title="Subscription Plan Credit Usage"
            items={subscriptionPlanUsage.items}
            used={subscriptionPlanUsage.used}
            total={subscriptionPlanUsage.total}
            disabled={isTopupPlan}
            categories={categoryCredits}
            renewsOn={summaryCards.renewsOn}
            nextResetInDays={summaryCards.cycleResetDays}
            billingCycle={summaryCards.billingCycle}
          />
          <SubscriptionCategoryUsage
            title="Top-up Category Credit Usage"
            categories={categoryCredits}
            billingMode="subscription"
            topupPlan
          />
          <TitledPanel
            title="Credit Spend by Month"
            extra={(
              <TestSpendPeriodControl
                startDate={effectiveSpendRange?.startDate}
                endDate={effectiveSpendRange?.endDate}
                onPeriodChange={(startDate, endDate) => {
                  if (startDate) {
                    setSpendDateRange({ startDate, endDate: endDate || startDate });
                    return;
                  }
                  setSpendDateRange(billingSpendRange);
                }}
              />
            )}
          >
            <TestSpendByMonth
              startDate={effectiveSpendRange?.startDate}
              endDate={effectiveSpendRange?.endDate}
              groupBy="payment_method"
              location={selectedLocationKey === ALL_LOCATIONS_KEY ? "" : selectedLocationKey}
              allotment={summaryCards.monthlyTestCredits}
            />
          </TitledPanel>
          {/* <UsageMetricsOffersPanel onViewAll={onViewAllOffers} /> */}
          <TermSummary summaryCards={summaryCards} />
        </>
      )}
      <StartTopupPoolAlertModal
        open={topupPoolAlertOpen}
        onClose={() => setTopupPoolAlertOpen(false)}
        poolTests={topupPoolAlertTests || topupPlanUsage.total}
        billingCycle={summaryCards.billingCycle}
      />
    </div>
  );
}
